import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircle, EnvelopeSimple, Hourglass, WarningCircle, XCircle } from '@phosphor-icons/react';
import { useAuth } from '../contexts/AuthContext';

// BILLING-IDENTITY-SEPARATION-001 (task section 10) / 001.1 (section 1):
// this screen resolves a PUBLIC purchase. It deliberately never reads
// `useAuth().user` — the backend endpoint it polls
// (`GET /public/billing/purchase/{session_id}`) requires no
// authentication and returns nothing beyond a coarse status and a masked
// email, so an unrelated browser session (including an admin session)
// can never influence what's shown here.
//
// 001.1: the backend now reports a durable activation-delivery state
// machine instead of raw payment_status. This screen only ever claims an
// email was sent when the backend reports `activation_sent` — it must
// never say so while activation is merely pending or has failed.
const MAX_POLL_ATTEMPTS = 8;
const POLL_INTERVAL_MS = 2500;

const POLLING_STATUSES = new Set(['payment_pending', 'activation_pending']);

export default function PurchaseComplete() {
  const { api } = useAuth();
  const { t } = useTranslation();
  const [state, setState] = useState('loading');
  const [maskedEmail, setMaskedEmail] = useState(null);
  const attemptRef = useRef(0);
  const timeoutRef = useRef(null);

  const sessionId = new URLSearchParams(window.location.search).get('session_id') || '';

  const poll = useCallback(async () => {
    if (!sessionId) {
      setState('error');
      return;
    }
    try {
      const { data } = await api.get(`/public/billing/purchase/${sessionId}`);
      if (data.status === 'unknown') {
        setState('error');
        return;
      }
      setMaskedEmail(data.masked_email || null);
      if (!POLLING_STATUSES.has(data.status)) {
        // activation_sent, already_claimed, activation_failed, cancelled
        setState(data.status);
        return;
      }
      attemptRef.current += 1;
      if (attemptRef.current >= MAX_POLL_ATTEMPTS) {
        setState('pending');
        return;
      }
      timeoutRef.current = setTimeout(poll, POLL_INTERVAL_MS);
    } catch {
      attemptRef.current += 1;
      if (attemptRef.current >= MAX_POLL_ATTEMPTS) {
        setState('error');
        return;
      }
      timeoutRef.current = setTimeout(poll, POLL_INTERVAL_MS);
    }
  }, [api, sessionId]);

  useEffect(() => {
    poll();
    return () => { if (timeoutRef.current) clearTimeout(timeoutRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center p-6">
      <div className="card max-w-md w-full text-center py-10 px-6" data-testid="purchase-complete-screen">
        {state === 'loading' && (
          <div data-testid="purchase-complete-loading">
            <Hourglass size={40} className="mx-auto mb-4 text-[var(--text-muted)] animate-pulse" />
            <p className="text-sm text-[var(--text-secondary)]">{t('purchase.complete.loading')}</p>
          </div>
        )}

        {state === 'pending' && (
          <div data-testid="purchase-complete-pending">
            <Hourglass size={40} className="mx-auto mb-4 text-[var(--gradient-start)]" />
            <h1 className="text-xl font-bold mb-2">{t('purchase.complete.title')}</h1>
            <p className="text-sm text-[var(--text-secondary)]">{t('purchase.complete.pending')}</p>
          </div>
        )}

        {(state === 'activation_sent' || state === 'already_claimed') && (
          <div data-testid={state === 'activation_sent' ? 'purchase-complete-activation-sent' : 'purchase-complete-already-claimed'}>
            <CheckCircle size={40} weight="fill" className="mx-auto mb-4" color="var(--accent-green)" />
            <h1 className="text-xl font-bold mb-2">{t('purchase.complete.title')}</h1>
            <p className="text-sm text-[var(--text-secondary)] mb-3">{t('purchase.complete.body')}</p>
            {maskedEmail && (
              <p className="text-xs text-[var(--text-muted)] flex items-center justify-center gap-1.5">
                <EnvelopeSimple size={14} /> {maskedEmail}
              </p>
            )}
            {state === 'already_claimed' && (
              <p className="text-xs text-[var(--text-muted)] mt-3">{t('purchase.complete.alreadyClaimed')}</p>
            )}
          </div>
        )}

        {state === 'activation_failed' && (
          <div data-testid="purchase-complete-activation-failed">
            <WarningCircle size={40} className="mx-auto mb-4" color="var(--accent-pink)" />
            <h1 className="text-xl font-bold mb-2">{t('purchase.complete.title')}</h1>
            <p className="text-sm text-[var(--text-secondary)]">{t('purchase.complete.activationFailed')}</p>
          </div>
        )}

        {state === 'cancelled' && (
          <div data-testid="purchase-complete-cancelled">
            <XCircle size={40} className="mx-auto mb-4" color="var(--accent-pink)" />
            <h1 className="text-xl font-bold mb-2">{t('purchase.complete.title')}</h1>
            <p className="text-sm text-[var(--text-secondary)]">{t('purchase.complete.cancelled')}</p>
          </div>
        )}

        {state === 'error' && (
          <div data-testid="purchase-complete-error">
            <WarningCircle size={40} className="mx-auto mb-4" color="var(--accent-pink)" />
            <h1 className="text-xl font-bold mb-2">{t('purchase.complete.title')}</h1>
            <p className="text-sm text-[var(--text-secondary)]">{t('purchase.complete.error')}</p>
          </div>
        )}

        <a href="/" className="btn-ghost inline-block mt-6 text-sm">{t('purchase.complete.backHome')}</a>
      </div>
    </div>
  );
}
